import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from 'react-redux'
import { Modal, Checkbox, Button, Progress, Space, Typography, Alert, Collapse } from "antd";
import JSZip, { forEach } from "jszip";
import { SubjectRepository, RdfTriplet, VocabsRepository } from '../../redux';
import { lastElementCardURI } from '../../utils/uriUtil';

const { Text, Link, Title } = Typography;

const OPT = {
  CARD: "card_individual",
  RELATED: "relatedRefs"
};

const LABELS = {
  [OPT.RELATED] : "Включить в экспорт детальную информацию о связанных элементах (при наличии)"
};

const defaultChecked = [OPT.CARD, OPT.RELATED];

function ExportIndividual({
  open,
  onClose,
  dispatch,
  singleIRI = false,
  IRI = null,
  conditions = [],
  rdfTypes = [],
  graph = "http://localhost/sksa/graph"
}) {
  const [checked, setChecked] = useState(defaultChecked);

  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState("");
  const [error, setError] = useState(null);

  const [zipBlob, setZipBlob] = useState(null);
  const [zipUrl, setZipUrl] = useState("");

  // --- NEW: Log ---
  const [logLines, setLogLines] = useState([]);
  const [logCollapsed, setLogCollapsed] = useState(true); // управляем авто-сворачиванием
  
  const downloadTriggeredRef = useRef(false);

  const isExportDone = !!zipUrl;

  const zipFileName = useMemo(() => {
    const fileName = IRI ? lastElementCardURI(IRI) : "all";
    const stamp = new Date().toISOString().replaceAll(":", "-").replaceAll(".", "-");
    return `export_individual_${fileName}_${stamp}.zip`;
  }, [IRI]);


  // --- Cleanup object URL ---
  useEffect(() => {
    return () => {
      if (zipUrl) URL.revokeObjectURL(zipUrl);
    };
  }, [zipUrl]);

  // При открытии: дефолты, убрать недоступные пункты
  useEffect(() => {
    if (!open) return;

    setChecked(() => {
      const next = new Set(defaultChecked);
      return Array.from(next);
    });

    resetRuntimeState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function resetRuntimeState() {
    setIsExporting(false);
    setProgress(0);
    setStatusText("");
    setError(null);
    downloadTriggeredRef.current = false;

    // NEW: reset log
    setLogLines([]);
    setLogCollapsed(true);

    if (zipUrl) {
      URL.revokeObjectURL(zipUrl);
      setZipUrl("");
    }
  }

  function pushLog(line) {
    const ts = new Date().toISOString().slice(11, 19);
    setLogLines((prev) => [...prev, `[${ts}] ${line}`]);
  }

  function buildTasks(IRIs) {
    const selected = new Set(checked);
    const list = [];

    const exportIRIs = uniqueIRIs(Array.isArray(IRIs) && IRIs.length ? IRIs : (IRI ? [IRI] : []));

    const taskIRIs = uniqueIRIs(exportIRIs);
    for (let index = 0; index < taskIRIs.length; index++) {
      const IRI = exportIRIs[index];
      list.push({
        key: OPT.CARD,
        includeRelated: selected.has(OPT.RELATED),
        info: {"individual": IRI, "IRIs": [IRI]},
        id: ""+(index+1)+"_"+lastElementCardURI(IRI),
        title: "Получение данных "+ lastElementCardURI(IRI),
        run: () => SubjectRepository.exportSubject(graph, [IRI]),
      });
    }
    
    return list;
  }

  // --- tasks for export ---
  const tasks = useMemo(() => {
    return buildTasks(singleIRI ? [IRI] : []);
  }, [checked, singleIRI, IRI, conditions]);

  async function handleExport() {
    setError(null);
    setIsExporting(true);
    setProgress(0);
    setStatusText("Подготовка…");
    setZipBlob(null);

    // лог разворачиваем
    setLogCollapsed(false);
    pushLog("Старт экспорта ZIP.");

    if (zipUrl) {
      URL.revokeObjectURL(zipUrl);
      setZipUrl("");
    }

    try {
      const lines = [];
      const info = [];
      const zip = new JSZip();

      let exportIRIs = [IRI];
      if (!singleIRI) {
        setStatusText("\u041f\u043e\u043b\u0443\u0447\u0435\u043d\u0438\u0435 \u0441\u043f\u0438\u0441\u043a\u0430 IRI\u2026");
        pushLog("VocabsRepository.loadIRIVocabs");
        exportIRIs = await dispatch(VocabsRepository.loadIRIVocabs(conditions, rdfTypes));
      }
      const exportTasks = buildTasks(exportIRIs);

      const headerLines = [
        singleIRI ? `Export for individual: ${IRI}` : "Export for vocabs individuals",
        `Generated at: ${new Date().toISOString()}`,
        `Selected sections: ${checked.length ? checked.join(", ") : "(none)"}`,
        "",
      ];
      lines.push(...headerLines);

      const total = exportTasks.length;
      for (let i = 0; i < exportTasks.length; i += 1) {
        const t = exportTasks[i];
        setStatusText(`Экспорт: ${t.title}…`);
        pushLog(`Экспорт: ${t.title}`);

        if (t.includeRelated){
          pushLog(`Поиск связанных объектов в ${t.id}`);
          const related = await RdfTriplet.loadSubject(t.info.individual, graph);
          const relatedIRIs = related.bindings
            .filter(item => item.predicat.value !== "http://www.w3.org/1999/02/22-rdf-syntax-ns#type")
            .filter(item => item.object.type === "uri")
            .map(item => item.object.value);
          const IRIs = [t.info.individual, ...relatedIRIs];
          if (IRIs.length>1){
            t.info.IRIs = IRIs;
            t.run = () => SubjectRepository.exportSubject(graph, IRIs)
          };
        };

        const result = await t.run();

        lines.push(`=== ${t.title} ===`);

        if (t.key === OPT.CARD) {
          const file = `${t.id}.ttl`;
          info.push({"card":t.info, "file":file});
          zip.file(file, `${result}\n`);
          pushLog(`Добавлен файл: ${file}`);
          lines.push(`==> Добавлен файл: ${file}`);
        }

        lines.push("");

        const pct = Math.round(((i + 1) / total) * 90);
        setProgress(pct);
      }

      setStatusText("Упаковка ZIP…");
      pushLog("Упаковка ZIP…");

      zip.file("export.log", lines.join("\n"));
      zip.file("export.json", JSON.stringify(info));

      const blob = await zip.generateAsync({ type: "blob" }, (meta) => {
        const pct = 90 + Math.round((meta.percent / 100) * 10);
        setProgress(Math.min(100, Math.max(90, pct)));
      });

      setZipBlob(blob);

      const url = URL.createObjectURL(blob);
      setZipUrl(url);
      setStatusText("ZIP готов. Начинаю скачивание…");
      setProgress(100);

      pushLog(`ZIP сформирован: ${zipFileName}`);
      triggerDownload(url, zipFileName);
      downloadTriggeredRef.current = true;

      setIsExporting(false);
      setStatusText("Готово.");
      pushLog("Экспорт завершён.");

      // После окончания экспорта лог свёртывается
      setLogCollapsed(true);

    } catch (e) {
      setIsExporting(false);
      setProgress(0);
      setStatusText("");
      setError(e?.message || String(e));
      pushLog(`Ошибка экспорта: ${e?.message || String(e)}`);
      // при ошибке можно оставить лог раскрытым
      setLogCollapsed(false);
    }
  }

  function handleClose() {
    resetRuntimeState();
    onClose?.();
  }

  const primaryButtonLabel = isExportDone ? "Закрыть" : "Экспорт";
  const primaryOnClick = isExportDone ? handleClose : handleExport;

  // Показывать блок прогресса, если идёт экспорт или идёт проверка графов или экспорт завершён
  const showProgressBlock = isExporting || isExportDone;

  const logItems = [
    {
      key: "log",
      label: "Журнал выполнения",
      children: (
        <div
          style={{
            maxHeight: 220,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
            fontSize: 12,
            lineHeight: 1.35,
            background: "#fafafa",
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: 10,
          }}
        >
          {logLines.length ? logLines.join("\n") : "Лог пуст."}
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={<Title level={5} style={{ margin: 0 }}>Экспорт</Title>}
      open={open}
      onCancel={handleClose}
      maskClosable={!isExporting}
      closable={!isExporting}
      destroyOnHidden={false}
      footer={
        <Space>
          {!isExportDone ? (
            <Button onClick={handleClose} disabled={isExporting}>
              Отмена
            </Button>
          ) : null}
          <Button
            type="primary"
            onClick={primaryOnClick}
            loading={isExporting}
            disabled={isExporting || (!zipUrl && (IRI === undefined && Array.isArray(conditions)))}
          >
            {primaryButtonLabel}
          </Button>
        </Space>
      }
    >
      <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
        <Text type="secondary">
          Выберите, какие разделы включить в экспорт:
        </Text>

        <Space orientation="vertical" size={8} style={{ width: "100%" }}>

          <Checkbox
            checked={checked.includes(OPT.RELATED)}
            onChange={(e) => toggleChecked(OPT.RELATED, e.target.checked, checked, setChecked)}
            disabled={isExporting || isExportDone }
          >
            {LABELS[OPT.RELATED]}
          </Checkbox>

        </Space>

        {showProgressBlock ? (
          <Space orientation="vertical" style={{ width: "100%" }}>
            {/* Экспортный прогресс */}
            {(isExporting || isExportDone) ? (
              <>
                <Progress percent={progress} />
                {statusText ? <Text>{statusText}</Text> : null}
              </>
            ) : null}

            {/* Лог под прогрессбаром */}
            <Collapse
              items={logItems}
              activeKey={logCollapsed ? [] : ["log"]}
              onChange={(keys) => setLogCollapsed(!(Array.isArray(keys) ? keys.includes("log") : false))}
            />
          </Space>
        ) : null}

        {error ? (
          <Alert
            type="error"
            showIcon
            message="Ошибка экспорта"
            description={<Text>{error}</Text>}
          />
        ) : null}

        {isExportDone ? (
          <Space orientation="vertical" style={{ width: "100%" }}>
            <Text strong>Ссылка на скачивание:</Text>
            <Link href={zipUrl} download={zipFileName}>
              Скачать {zipFileName}
            </Link>
            <Text type="secondary">
              Если скачивание не началось автоматически, нажмите ссылку выше.
            </Text>
          </Space>
        ) : null}
      </Space>
    </Modal>
  );
}

// --- helpers ---

function toggleChecked(key, next, checked, setChecked) {
  const set = new Set(checked);
  if (next) set.add(key);
  else set.delete(key);
  setChecked(Array.from(set));
}

function isPlainEmptyObject(v) {
  if (!v || typeof v !== "object") return true;
  if (Array.isArray(v)) return v.length === 0;
  return Object.keys(v).length === 0;
}

function uniqueIRIs(IRIs) {
  return [...new Set((Array.isArray(IRIs) ? IRIs : []).filter(Boolean))];
}
function triggerDownload(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// Redux mapping
const mapStateToProps = (state, ownProps) => {
  const { vocabs, vset } = state;
  const singleIRI = ownProps.IRI !== undefined && ownProps.IRI !== null && ownProps.IRI !== "";
  return {
    ...ownProps,
    singleIRI,
    IRI: singleIRI ? ownProps.IRI : null,
    conditions: ownProps.conditions ?? [],
    rdfTypes: ownProps.rdfTypes ?? vocabs.rdfTypes ?? [],
    graph: vset.graph.signed
  };
};

export default connect(mapStateToProps)(ExportIndividual);
